import React, { useState, Fragment } from 'react'
import { PopoverPosition } from 'react-tiny-popover'
import {
	Image,
	isMultipleChoice,
	isMultipleChoiceAnswer,
	isMultipleSelectAnswer,
} from '../../utils'
import Popover from '../../components/popover'
import ChoiceBox from '../../components/choice-box'
import { Thumbnail } from '../../assets/img'
import Footer from '../../components/studentPromptFooter'
import StudentReviewWrapper from '../../components/student-review'
import {
	MultipleChoice,
	ClientFields,
	QuestionChoice,
	StudentAnswer,
	MultipleSelect,
	variantTypes,
} from '@mission.io/question-toolkit'
import { QuestionVariant, ClassResponse } from '../../model/general'
import { MultipleChoiceProps } from './MultipleChoice'
import {
	LabelInformation,
	ChoiceWrapper,
	ImageViewer,
	GeneralScoreTitle,
} from '../../assets/styles'
import { BsCheck } from 'react-icons/bs'
import { MdClose } from 'react-icons/md'
import { FaRegQuestionCircle, FaRegTimesCircle } from 'react-icons/fa'
import 'styled-components/macro'
import { styled, useTheme } from 'styled-components'
import FooterStickyContainer from '../../components/footer-sticky-container/FooterStickyContainer'
import OptionalMarkdown from '../../components/Markdown/OptionalMarkdown'
import { _tempTheme } from '../../utils/tempTheme'

/**
 * A component for a student view where the student can answer the multiple choice question. It also
 * has a review view where the student can see the correct answer of the question
 * @returns {JSX.Element}
 */
export const StudentMultipleChoice = (
	props: MultipleChoiceProps
): JSX.Element => {
	const {
		question,
		textToSpeech: TextToSpeech,
		requestId,
		review,
		sendMessage,
		studentResponse,
		parentRef,
		classResponse,
	} = props
	if (!question || !isMultipleChoice(question)) return <Fragment />

	const [selectedChoiceId, setSelectedChoiceId] = useState<string>('')
	const [error, setError] = useState<string>('')

	const choicesHaveImage = Boolean(
		question?.choices.some((choice: QuestionChoice) => !!choice.media?.[0])
	)

	const handleSubmit = () => {
		if (!selectedChoiceId) {
			setError('Please Make a Choice')
			return
		}
		setError('')
		sendMessage &&
			sendMessage({
				type: QuestionVariant.MULTIPLE_CHOICE,
				value: selectedChoiceId,
			})
	}
	let studentAnswer: string | undefined = selectedChoiceId
	const instantReview = review === 'INSTANT'
	if (instantReview) {
		if (!studentResponse || !isMultipleChoiceAnswer(studentResponse.answer)) {
			studentAnswer = undefined
		} else {
			studentAnswer = studentResponse?.answer.value
		}
	}

	if (question)
		return instantReview || (!review && requestId) ? (
			<FooterStickyContainer
				instantReview={instantReview}
				footerContent={
					<Footer
						isReview={instantReview}
						error={error}
						onClickSubmit={handleSubmit}
						question={question}
					/>
				}
				topContent={
					<>
						{classResponse && (
							<GeneralScoreTitle>
								<div className="question-index">{`${props.questionIndex}.`}</div>
								{`${(
									classResponse.generalScore * 100
								).toFixed()}% Answered Correctly`}
							</GeneralScoreTitle>
						)}
						<LabelInformation>
							<OptionalMarkdown
								textToSpeech={TextToSpeech}
								className="prose-on-game-light-purple">
								{question.phrase}
							</OptionalMarkdown>
						</LabelInformation>
						<Image
							media={question.media}
							expandable
							parentRef={parentRef}
							className="!max-h-64"
						/>

						<ChoiceWrapper $withImage={choicesHaveImage}>
							{question.choices.map((choice: QuestionChoice, i) => {
								const selected = studentAnswer === String(choice._id)
								const optionScore =
									classResponse &&
									classResponse.scores.type === variantTypes.MULTIPLE_CHOICE &&
									classResponse.scores.optionScores.find(
										i => i._id === String(choice._id)
									)?.score

								return (
									<ChoiceBox
										key={String(choice._id)}
										isReview={instantReview}
										selected={selected}
										onClick={() => {
											setSelectedChoiceId(String(choice._id))
											setError('')
										}}
										choice={choice}
										textToSpeech={TextToSpeech}
										classResponse={classResponse}
										parentRef={parentRef}
										type="MULTIPLE_CHOICE"
										optionScore={optionScore || 0}
										index={i}
									/>
								)
							})}
						</ChoiceWrapper>
					</>
				}
			/>
		) : review === 'OVERVIEW' ? (
			<ReviewMultipleChoice
				index={props.questionIndex}
				studentAnswer={props.studentResponse?.answer}
				question={question}
			/>
		) : (
			<Fragment />
		)

	return <Fragment />
}

/**
 * When a multiple choice or multiple select question is reviewed immediately after it is answered
 * this tag will appear in the corner of a question choice that was incorrectly assessed.
 * The tag indicates a choice that is incorrectly selected and a choice that should have been selected but was not.
 * IMPORTANT:
 * This component must be a child of the ChoiceBox component in order for styles to be attributed properly.
 * @param {boolean}  props.selected if the question choice was selected by a student
 * @param {boolean}  props.correct if the question choice is a correct choice
 * @returns {JSX.Element} the tag to be displayed in the choice box.
 */
export const ReviewTag = ({
	selected,
	correct,
	type,
	parentRef,
}: {
	selected: boolean
	correct: boolean
	parentRef: React.MutableRefObject<HTMLElement | undefined> | undefined
	type: 'MULTIPLE_CHOICE' | 'MULTIPLE_SELECT'
}): JSX.Element | null => {
	const sharedPopoverProps = {
		parentElement: parentRef?.current,
		padding: 4,
		arrowColor: 'black',
		positions: ['top'] as PopoverPosition[],
	}
	const rightAnswerHelpText =
		type === 'MULTIPLE_CHOICE'
			? 'The correct choice'
			: 'This choice was correct, but you did not select it.'
	const wrongAnswerHelpText =
		type === 'MULTIPLE_CHOICE'
			? 'The choice you selected (wrong)'
			: 'This choice is incorrect, but you selected it.'

	const tagIcon =
		!selected && correct ? (
			<Popover
				{...sharedPopoverProps}
				trigger={<FaRegQuestionCircle size={20} color="white" />}>
				<ReviewTagSpan>{rightAnswerHelpText}</ReviewTagSpan>
			</Popover>
		) : selected && !correct ? (
			<Popover
				{...sharedPopoverProps}
				trigger={<FaRegTimesCircle size={20} color="white" />}>
				<ReviewTagSpan>{wrongAnswerHelpText}</ReviewTagSpan>
			</Popover>
		) : null

	return tagIcon ? <div className="tag">{tagIcon}</div> : null
}

const ReviewTagSpan = styled.span`
	background-color: black;
	color: white;
	border-radius: 4px;
	padding: 4px;
	font-family: ${({ theme }) => theme.fontFamily};
`

export function ReviewMultipleChoice({
	question,
	studentAnswer,
	index,
}: {
	question: MultipleChoice<ClientFields> | MultipleSelect<ClientFields>
	studentAnswer?: StudentAnswer
	classResponse?: ClassResponse | null
	index?: number
}): JSX.Element {
	if (
		studentAnswer &&
		!(
			isMultipleChoiceAnswer(studentAnswer) ||
			isMultipleSelectAnswer(studentAnswer)
		)
	)
		return <Fragment />

	const theme = useTheme()

	return (
		<StudentReviewWrapper
			index={index}
			prompt={question.phrase}
			media={question.media}>
			<div css="flex: 1; text-align: left; margin-top: 8px; transform: translateX(-8px);">
				{question.choices.map((option: QuestionChoice) => {
					const chosen = studentAnswer
						? isMultipleChoiceAnswer(studentAnswer)
							? studentAnswer?.value === String(option._id)
							: studentAnswer.value.indexOf(String(option._id)) > -1
						: false

					const correct = option.answer

					return (
						<Option
							key={String(option._id)}
							$chosen={chosen}
							$correct={correct}>
							<Bullet
								$correct={correct}
								$chosen={chosen}
								$isMultipleChoice={isMultipleChoice(question)}>
								{correct ? (
									<BsCheck
										aria-label="Correct Choice"
										className="icon-success"
									/>
								) : chosen ? (
									<MdClose
										aria-label="Incorrect Choice"
										className="icon-wrong"
									/>
								) : (
									<div className="no-icon"></div>
								)}
							</Bullet>
							<div style={{ display: 'inline-flex' }}>
								{option.media?.[0] && (
									<Popover
										arrowColor={theme.white}
										trigger={
											<img
												alt={`Media for option: ${option.text}`}
												src={Thumbnail}
												css={`
													width: ${_tempTheme.spacing4};
													height: ${_tempTheme.spacing4};
													margin-right: ${_tempTheme.spacing2};
													transform: translateY(1px);
													cursor: pointer;
												`}
											/>
										}>
										<ImageViewer
											alt={`Media for question: ${question.phrase}`}
											src={option.media?.[0].url}
										/>
									</Popover>
								)}
								<OptionalMarkdown className="prose-on-game-light-purple">
									{option.text}
								</OptionalMarkdown>
								{!studentAnswer && correct && (
									<i
										css={`
											margin-left: ${_tempTheme.spacing2};
										`}>
										- Did not answer
									</i>
								)}
							</div>
						</Option>
					)
				})}
			</div>
		</StudentReviewWrapper>
	)
}

export const MinimalCenteredModalStyle = {
	padding: '0',
	border: 'none',
	width: 'fit-content',
	left: '50%',
	top: '50%',
	transform: 'translate(-50%, -50%)',
	right: '0',
	boxShadow: 'none',
	height: 'fit-content',
	justifyContent: 'center',
	alignItems: 'center',
}

interface IChoice {
	$correct?: boolean
	$chosen?: boolean
	$selected?: boolean
	$isMultipleChoice?: boolean
}

export const Bullet = styled.div<IChoice>`
	margin: 0 ${_tempTheme.spacing2};
	transform: translateY(1px);
	border-radius: ${({ theme, $isMultipleChoice }) =>
		$isMultipleChoice ? '50%' : theme.spacingHalf};
	background-color: transparent;
	display: flex;

	${({ theme, $correct, $chosen, $isMultipleChoice }) => {
		if ($isMultipleChoice) {
			if ($correct) {
				return `background-color: ${theme.success};`
			} else if ($chosen && !$correct) {
				return `background-color: ${theme.error};`
			} else {
				return `background-color: ${theme.lighterGreyColor};`
			}
		} else {
			if ($correct) {
				return `border: 3px solid ${theme.success};`
			} else if ($chosen && !$correct) {
				return `border: 3px solid ${theme.error};`
			} else {
				return `border: 3px solid  ${theme.lighterGreyColor};`
			}
		}
	}}

	.icon-success {
		color: ${({ theme, $isMultipleChoice }) =>
			$isMultipleChoice ? theme.white : theme.success};
	}
	.icon-wrong {
		color: ${({ theme, $isMultipleChoice }) =>
			$isMultipleChoice ? theme.white : theme.error};
	}
	.no-icon {
		height: ${_tempTheme.spacing4};
		width: ${_tempTheme.spacing4};
	}
`
export const Option = styled.li<IChoice>`
	padding: 1px 0;
	margin-top: 2px;
	display: flex;
	align-items: center;
	border-radius: ${_tempTheme.spacing1};
	&:not(:last-child) {
		margin-bottom: 2px;
	}
	${({ $correct, $chosen }) => {
		if ($chosen && $correct) {
			return `background-color: rgba(111, 211, 127, 0.61);`
		}
		if ($chosen && !$correct) {
			return `background-color: rgba(255, 143, 143, 0.61);`
		}
	}}
`

export default StudentMultipleChoice
